// DO Applications Management Component - Converted from .NET rptrAppsList_DataBind
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Divider,
    Chip,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Badge,
    Card,
    CardContent,
    Autocomplete
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    VerifiedUser as VerifiedUserIcon,
    Visibility as VisibilityIcon,
    GetApp as GetAppIcon,
    History as HistoryIcon,
    Refresh as RefreshIcon,
    AllInclusive as AllInclusiveIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import DoApiService from '../../../services/DoApiService';
import { encryptAppId, encryptText } from '../../../utils/encryption';
import * as XLSX from 'xlsx';

// Helper functions for displaying data
const getApplicationTypeName = (assessmentTypeId) => {
    const types = {
        1: 'New Application',
        2: 'Renewal',
        3: 'Extension',
        4: 'Surveillance',
        5: 'Re-assessment',
        6: 'Special',
        7: 'Others'
    };
    return types[assessmentTypeId] || 'Unknown';
};

const getDisciplineName = (labTypeId) => {
    const disciplines = {
        1: 'Testing',
        2: 'Calibration',
        7: 'Medical'
    };
    return disciplines[labTypeId] || 'N/A';
};

const getGroupName = (labTypeId) => {
    const groups = {
        1: 'Physical/Chemical',
        2: 'Electrical/Electronics',
        7: 'Pathology'
    };
    return groups[labTypeId] || 'N/A';
};


const SubmittedApplicationSection = () => {
    // Router hooks
    const navigate = useNavigate();

    // State management
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedButtonType, setSelectedButtonType] = useState('btnApplicantLab');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [summary, setSummary] = useState(null);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [applicationHistory, setApplicationHistory] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [labIdFilter, setLabIdFilter] = useState('all');
    const [labNameFilter, setLabNameFilter] = useState('all');
    const [stateFilter, setStateFilter] = useState('all');
    const [daysPassedFilter, setDaysPassedFilter] = useState('all');
    const [workableFilesFilter, setWorkableFilesFilter] = useState(false);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [exporting, setExporting] = useState(false);
    const pageSize = 50;

    // Get user data from localStorage (similar to Session in .NET)
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData.orgId;
    const userTypeId = userData.userTypeId;

    // Load applications based on button type (equivalent to .NET switch statement)
    const loadApplications = useCallback(async (buttonType = selectedButtonType, pageNum = page) => {
        if (!DoApiService.validateUserSession()) {
            setError('User session invalid. Please login again.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const data = await DoApiService.getApplicationsList({
                buttonType,
                orgId,
                userTypeId,
                page: pageNum,
                limit: 50
            });

            if (data.success) {
                setApplications(data.data.applications || []);
                setTotalCount(data.data.totalCount || 0);
                setTotalPages(data.data.totalPages || 1);
                setPage(pageNum);
                setSelectedButtonType(buttonType);
            } else {
                throw new Error(data.message || 'Failed to load applications');
            }
        } catch (err) {
            setError(`Error loading applications: ${err.message}`);
            console.error('Load applications error:', err);
        } finally {
            setLoading(false);
        }
    }, [orgId, userTypeId, selectedButtonType, page]);

    // Load applications summary
    const loadSummary = useCallback(async () => {
        if (!DoApiService.validateUserSession()) return;

        try {
            const data = await DoApiService.getApplicationsSummary({
                orgId,
                userTypeId
            });

            if (data.success) {
                setSummary(data.data);
            }
        } catch (err) {
            console.error('Load summary error:', err);
        }
    }, [orgId, userTypeId]);

    // View application details
    const viewApplicationDetails = async (appId) => {
        try {
            const data = await DoApiService.getApplicationDetails({
                appId,
                orgId,
                userTypeId
            });

            if (data.success) {
                setSelectedApplication(data.data);
                setDetailsDialogOpen(true);
            }
        } catch (err) {
            console.error('View details error:', err);
        }
    };




    // View application details - navigate to dedicated view page (equivalent to .NET viewApp command)
    const viewApplication = (app) => {
        try {
            // Encrypt parameters similar to .NET Session variables
            const encryptedAppId = encryptAppId(app.appId);
            const encryptedOrgId = encryptText(app.orgId?.toString() || '');
            const encryptedLabTypeId = encryptText(app.labTypeId?.toString() || '');
            const encryptedlaboratoryName = encryptText(app.laboratoryName || '');

            console.log('Encrypted parameters:', {
                appId: encryptedAppId,
                orgId: encryptedOrgId,
                labTypeId: encryptedLabTypeId
            });

            // Build URL with encrypted parameters (equivalent to .NET Session["AppId"], Session["OrgIdCab"], Session["LabTypeId"])
            const queryParams = new URLSearchParams({
                appId: encryptedAppId,
                orgId: encryptedOrgId,
                labTypeId: encryptedLabTypeId,
                laboratoryName: encryptedlaboratoryName
            });

            const targetUrl = `/main/do/applications/view?${queryParams.toString()}`;
            console.log('Target URL:', targetUrl);

            // Navigate to ViewApplication page (equivalent to .NET Response.Redirect("Viewapplication"))
            navigate(targetUrl);
        } catch (err) {
            console.error('Navigate to view application error:', err);
        }
    };

    const viewSCFDashboard = (app) => {
        try {
            const targetUrl = `https://nablwpnew.qci.org.in/SCF_Integration/Transfer.aspx?user=Do&orgid=${localStorage.getItem("orgId")}&oid=${app.orgId?.toString()}`;
            console.log('Target URL:', targetUrl);
            window.location.href = targetUrl;
        } catch (err) {
            console.error('Navigate to view application error:', err);
        }
    };

    // Handle button click (equivalent to .NET ViewState["btnClicks"])
    const handleButtonClick = (buttonType) => {
        setPage(1);
        // Clear all filters when switching button types
        setWorkableFilesFilter(false);
        setStatusFilter('all');
        setLabIdFilter('all');
        setLabNameFilter('all');
        setStateFilter('all');
        setDaysPassedFilter('all');
        loadApplications(buttonType, 1);
    };

    // Handle page change
    const handlePageChange = (event, newPage) => {
        loadApplications(selectedButtonType, newPage);
    };



    // Format date (show only dd/mm/yyyy without time)
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';

        // Handle array of dates (take first element)
        if (Array.isArray(dateString)) {
            dateString = dateString[0];
        }

        try {
            // Parse the date string
            const date = new Date(dateString);

            // Check if date is valid
            if (isNaN(date.getTime())) {
                // Try parsing different formats
                if (typeof dateString === 'string') {
                    // Handle format like "09 Sep 2025 05:03:34:943"
                    const dateParts = dateString.split(' ');
                    if (dateParts.length >= 3) {
                        const dateOnly = `${dateParts[0]} ${dateParts[1]} ${dateParts[2]}`;
                        const parsedDate = new Date(dateOnly);
                        if (!isNaN(parsedDate.getTime())) {
                            return parsedDate.toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                            });
                        }
                    }
                }
                return 'Invalid Date';
            }

            // Return in dd/mm/yyyy format
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid Date';
        }
    };

    // Format date for assessment (cleaner format without time)
    const formatAssessmentDate = (dateString) => {
        if (!dateString) return 'Not Scheduled';

        // Handle array of dates
        if (Array.isArray(dateString)) {
            dateString = dateString[0];
        }

        let date;

        // Check if the date string is in DD/MM/YYYY format
        if (typeof dateString === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('/');
            // Convert to ISO format (YYYY-MM-DD) for proper parsing
            date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
        } else {
            date = new Date(dateString);
        }

        if (isNaN(date.getTime())) return 'Invalid Date';

        // Return date in DD MMM YYYY format (e.g., "22 Dec 2025")
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // Calculate days passed from status date
    const calculateDaysPassed = (statusDate) => {
        if (!statusDate) return 'N/A';

        const status = new Date(statusDate);
        const today = new Date();

        // Reset time to midnight for accurate day calculation
        status.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const diffTime = today - status;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        return diffDays;
    };

    // Get unique statuses from applications
    const getUniqueStatuses = () => {
        const statuses = new Set();
        applications.forEach(app => {
            const status = app.status || app.stage;
            if (status) {
                statuses.add(status);
            }
        });
        return Array.from(statuses).sort();
    };

    // Get unique Lab IDs from applications
    const getUniqueLabIds = () => {
        const labIds = new Set();
        applications.forEach(app => {
            const labId = app.applicationNo;
            if (labId) {
                labIds.add(labId);
            }
        });
        return Array.from(labIds).sort();
    };

    // Get unique Laboratory Names from applications
    const getUniqueLabNames = () => {
        const labNames = new Set();
        applications.forEach(app => {
            const labName = app.laboratoryName;
            if (labName) {
                labNames.add(labName);
            }
        });
        return Array.from(labNames).sort();
    };

    // Get unique States from applications
    const getUniqueStates = () => {
        const states = new Set();
        applications.forEach(app => {
            const state = app.state;
            if (state) {
                states.add(state);
            }
        });
        return Array.from(states).sort();
    };

    // Get unique Days Passed ranges
    const getDaysPassedOptions = () => {
        return [
            '0 days',
            '1 day',
            '2 days',
            '3+ days',
            '5+ days',
            '7+ days',
            '10+ days',
            '15+ days',
            '30+ days'
        ];
    };

    // Helper function to check if an application is a workable file
    const isWorkableFile = (app) => {
        const stage = app.stage || app.status || '';
        return stage === '18' || stage === '6c' || stage === '6b' || stage === '18b' || stage === '18c' || stage === '19a' ||
            stage === 18 || stage.toString().toLowerCase() === '18b' || stage.toString().toLowerCase() === '19h' || stage.toString().toLowerCase() === '19f' ||
            stage.toString().toLowerCase() === '18c' || stage.toString().toLowerCase() === '19d' || stage.toString().toLowerCase() === '21';
    };

    // Helper function to check if days passed matches filter
    const matchesDaysPassedFilter = (app, filter) => {
        const stage = app.stage || app.status || '';
        const shouldCheckDays = stage === '18' || stage === '18b' || stage === '18c' || stage === '19a' ||
            stage === 18 || stage.toString().toLowerCase() === '18b' ||
            stage.toString().toLowerCase() === '18c';

        if (!shouldCheckDays) return false;

        const days = calculateDaysPassed(app.lastUpdated[1] || app.applicationDate);
        if (typeof days !== 'number') return false;

        switch (filter) {
            case '0 days': return days === 0;
            case '1 day': return days === 1;
            case '2 days': return days === 2;
            case '3+ days': return days >= 3;
            case '5+ days': return days >= 5;
            case '7+ days': return days >= 7;
            case '10+ days': return days >= 10;
            case '15+ days': return days >= 15;
            case '30+ days': return days >= 30;
            default: return false;
        }
    };

    // Filter applications based on all filters
    useEffect(() => {
        let filtered = applications;

        // Apply workable files filter first
        // if (workableFilesFilter) {
        //     filtered = filtered.filter(app => isWorkableFile(app));
        // }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(app => {
                const appStatus = app.status || app.stage;
                return appStatus === statusFilter;
            });
        }

        // Apply lab ID filter
        if (labIdFilter !== 'all') {
            filtered = filtered.filter(app => {
                return app.applicationNo === labIdFilter;
            });
        }

        // Apply laboratory name filter
        if (labNameFilter !== 'all') {
            filtered = filtered.filter(app => {
                return app.laboratoryName === labNameFilter;
            });
        }

        // Apply state filter
        if (stateFilter !== 'all') {
            filtered = filtered.filter(app => {
                return app.state === stateFilter;
            });
        }

        // Apply days passed filter
        if (daysPassedFilter !== 'all') {
            filtered = filtered.filter(app => {
                return matchesDaysPassedFilter(app, daysPassedFilter);
            });
        }

        setFilteredApplications(filtered);
    }, [applications, statusFilter, labIdFilter, labNameFilter, stateFilter, daysPassedFilter, workableFilesFilter, matchesDaysPassedFilter]);

    // Handle status filter change
    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
    };

    // Load data on component mount
    useEffect(() => {
        loadApplications();
        loadSummary();
    }, []);

    // Refresh data
    const refreshData = () => {
        loadApplications();
        loadSummary();
    };

    // Export to Excel functionality
    const exportToExcel = async () => {
        setExporting(true);

        try {
            // 🔥 Fetch ALL data (ignore pagination)
            const response = await DoApiService.getApplicationsList({
                buttonType: selectedButtonType,
                orgId,
                userTypeId,
                page: 1,
                limit: 1000000 // 🔥 big number (or use totalCount)
            });

            if (!response.success) {
                throw new Error(response.message || 'Failed to fetch data');
            }

            let allData = response.data.applications || [];

            if (allData.length === 0) {
                setError('No data available to export');
                return;
            }

            // ✅ Apply same filters (important)
            let filtered = allData;

            if (statusFilter !== 'all') {
                filtered = filtered.filter(app => (app.status || app.stage) === statusFilter);
            }

            if (labIdFilter !== 'all') {
                filtered = filtered.filter(app => app.applicationNo === labIdFilter);
            }

            if (labNameFilter !== 'all') {
                filtered = filtered.filter(app => app.laboratoryName === labNameFilter);
            }

            if (stateFilter !== 'all') {
                filtered = filtered.filter(app => app.state === stateFilter);
            }

            if (daysPassedFilter !== 'all') {
                filtered = filtered.filter(app => matchesDaysPassedFilter(app, daysPassedFilter));
            }


            const exportData = filtered.map((app, index) => {

                const calculateDaysPassed = (statusDate) => {
                    if (!statusDate) return 'N/A';

                    const status = new Date(statusDate);
                    const today = new Date();

                    status.setHours(0, 0, 0, 0);
                    today.setHours(0, 0, 0, 0);

                    const diffTime = today - status;
                    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                    return diffDays;
                };

                const getApplicationTypeName = (assessmentTypeId) => {
                    const types = {
                        1: 'New Application',
                        2: 'Renewal',
                        3: 'Extension',
                        4: 'Surveillance',
                        5: 'Re-assessment',
                        6: 'Special',
                        7: 'Others'
                    };
                    return types[assessmentTypeId] || 'Unknown';
                };

                const formatExportDate = (dateString) => {
                    if (!dateString) return 'Not Scheduled';

                    if (Array.isArray(dateString)) {
                        dateString = dateString[0];
                    }

                    let date;

                    if (typeof dateString === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
                        const [day, month, year] = dateString.split('/');
                        date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
                    } else {
                        date = new Date(dateString);
                    }

                    if (isNaN(date.getTime())) return 'Invalid Date';

                    return date.toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                    });
                };

                const stage = app.stage || app.status || '';
                const shouldShowDays =
                    stage === '18' || stage === '18b' || stage === '18c' || stage === '19a' ||
                    stage === 18 || stage.toString().toLowerCase() === '18b' ||
                    stage.toString().toLowerCase() === '18c';

                const days = shouldShowDays
                    ? calculateDaysPassed(app.lastUpdated[1] || app.applicationDate)
                    : 'N/A';

                const daysPassed = typeof days === 'number' ? `${days} days` : days;

                return {
                    'Sr. No.': index + 1,
                    'Lab ID': app.applicationNo,
                    'User Name': app.loginName || app.contactPerson || 'N/A',
                    'Laboratory Name': app.laboratoryName,
                    'Standard': app.standard || '',
                    'Application Type': app.assessmentType || getApplicationTypeName(app.assessmentTypeId),
                    'Address': app.address || app.addressLine1 || 'N/A',
                    'City': app.city || 'N/A',
                    'State': app.state || 'N/A',
                    'Status': app.status || app.stage || 'Pending',
                    'Status Date': app.lastUpdated[0]
                        ? formatDate(app.lastUpdated[0])
                        : formatDate(app.applicationDate),
                    'Field': app.labType || 'N/A',
                    'Application Submitted on': formatDate(app.submitDate),
                    'Assessment Start Date': formatExportDate(app.assessmentStart),
                    'Assessment End Date': formatExportDate(app.assessment),
                    'Days Passed': daysPassed
                };
            });

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'All Data');

            XLSX.writeFile(wb, 'Full_Applications.xlsx');

        } catch (err) {
            setError(err.message);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                DO Applications Management
            </Typography>

            {/* Summary Cards */}
            {summary && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Total Applications
                                </Typography>
                                <Typography variant="h4">
                                    {summary.totalApplications}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    New Applications
                                </Typography>
                                <Typography variant="h4" color="primary">
                                    {summary.newApplications}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid> */}


                    <Grid item xs={12} sm={6} md={3}>
                        <Card
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                border: workableFilesFilter ? '2px solid #ff9800' : '1px solid #e0e0e0',
                                backgroundColor: workableFilesFilter ? '#fff3e0' : 'white',
                                '&:hover': {
                                    boxShadow: 4,
                                    transform: 'translateY(-2px)'
                                }
                            }}
                            onClick={() => {
                                const newValue = !workableFilesFilter;
                                setWorkableFilesFilter(newValue);

                                if (newValue) {
                                    setStatusFilter('all');
                                    setLabIdFilter('all');
                                    setLabNameFilter('all');
                                    setStateFilter('all');
                                    setDaysPassedFilter('all');
                                    loadApplications('btnWorkableFiles', 1);
                                } else {
                                    // reload normal data
                                    loadApplications(selectedButtonType, 1);
                                }
                            }}
                        >
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Workable Files
                                    {workableFilesFilter && (
                                        <Chip
                                            label="Active Filter"
                                            size="small"
                                            color="warning"
                                            sx={{ ml: 1, height: '16px', fontSize: '0.6rem' }}
                                        />
                                    )}
                                </Typography>
                                <Typography variant="h4" color="warning.main">
                                    {summary.workableFiles || 0}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Click to filter
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Files at Lab End
                                </Typography>
                                <Typography variant="h4" color="info.main">
                                    {summary.filesAtLabEnd || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Accredited Applications
                                </Typography>
                                <Typography variant="h4" color="success.main">
                                    {summary.accreditedApplications}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Action Buttons */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                    variant={selectedButtonType === 'btnApplicantLab' ? 'contained' : 'outlined'}
                    sx={{
                        backgroundColor: selectedButtonType === 'btnApplicantLab' ? '#2196F3' : 'transparent',
                        '&:hover': {
                            backgroundColor: selectedButtonType === 'btnApplicantLab' ? '#1976D2' : '#e3f2fd'
                        }
                    }}
                    onClick={() => handleButtonClick('btnApplicantLab')}
                    startIcon={<AddCircleOutlineIcon />}
                >
                    {["1", "2"].includes(localStorage.getItem("labTypeId"))? "Applicant Lab" : "New Applications"}
                    {summary && (
                        <Badge
                            badgeContent={summary.newApplications}
                            color="error"
                            sx={{ ml: 1.5 }}
                        />
                    )}
                </Button>

                <Button
                    variant={selectedButtonType === 'btnAccreditatedLab' ? 'contained' : 'outlined'}
                    sx={{
                        backgroundColor: selectedButtonType === 'btnAccreditatedLab' ? '#4CAF50' : 'transparent',
                        '&:hover': {
                            backgroundColor: selectedButtonType === 'btnAccreditatedLab' ? '#388E3C' : '#e8f5e8'
                        }
                    }}
                    onClick={() => handleButtonClick('btnAccreditatedLab')}
                    startIcon={<VerifiedUserIcon />}
                >
                    {["1", "2"].includes(localStorage.getItem("labTypeId"))? "Accredited Lab": "Accredited Applications"}
                    {summary && (
                        <Badge
                            badgeContent={summary.accreditedApplications}
                            color="success"
                            sx={{ ml: 1.5 }}
                        />
                    )}
                </Button>
                {/* <Button
                    variant={selectedButtonType === 'btnAllLab' ? 'contained' : 'outlined'}
                    sx={{
                        backgroundColor: selectedButtonType === 'btnAllLab' ? '#4CAF50' : 'transparent',
                        '&:hover': {
                            backgroundColor: selectedButtonType === 'btnAllLab' ? '#388E3C' : '#e8f5e8'
                        }
                    }}
                    onClick={() => handleButtonClick('btnAllLab')}
                    startIcon={<AllInclusiveIcon />}
                >
                    All Applications
                    {summary && (
                        <Badge
                            badgeContent={summary.accreditedApplications}
                            color="success"
                            sx={{ ml: 1 }}
                        />
                    )}
                </Button> */}

                <Button
                    variant={selectedButtonType === 'btnIlac' ? 'contained' : 'outlined'}
                    sx={{
                        backgroundColor: selectedButtonType === 'btnIlac' ? '#4CAF50' : 'transparent',
                        '&:hover': {
                            backgroundColor: selectedButtonType === 'btnIlac' ? '#388E3C' : '#e8f5e8'
                        }
                    }}
                    onClick={() => handleButtonClick('btnIlac')}
                    startIcon={<AllInclusiveIcon />}
                >
                    ILAC Applications
                    {summary && (
                        <Badge
                            badgeContent={summary.ilac}
                            color="success"
                            sx={{ ml: 1.5 }}
                        />
                    )}
                </Button>

                <Box sx={{ flexGrow: 1 }} />


                <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    startIcon={<GetAppIcon />}
                    disabled={loading || exporting || filteredApplications.length === 0}
                >
                    {exporting ? 'Exporting...' : 'Export to Excel'}
                </Button>

                <Button
                    variant="outlined"
                    onClick={refreshData}
                    startIcon={<RefreshIcon />}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {/* Filter Section */}
            {!loading && applications.length > 0 && (
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Autocomplete
                        sx={{ minWidth: 250 }}
                        size="small"
                        options={['All Statuses', ...getUniqueStatuses()]}
                        value={statusFilter === 'all' ? 'All Statuses' : statusFilter}
                        onChange={(event, newValue) => {
                            setStatusFilter(newValue === 'All Statuses' || newValue === null ? 'all' : newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Status"
                                placeholder="Type or select status"
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option === value}
                        clearOnEscape
                        disableClearable={false}
                    />

                    <Autocomplete
                        sx={{ minWidth: 250 }}
                        size="small"
                        options={['All Lab IDs', ...getUniqueLabIds()]}
                        value={labIdFilter === 'all' ? 'All Lab IDs' : labIdFilter}
                        onChange={(event, newValue) => {
                            setLabIdFilter(newValue === 'All Lab IDs' || newValue === null ? 'all' : newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Lab ID"
                                placeholder="Type or select Lab ID"
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option === value}
                        clearOnEscape
                        disableClearable={false}
                    />

                    <Autocomplete
                        sx={{ minWidth: 250 }}
                        size="small"
                        options={['All Laboratories', ...getUniqueLabNames()]}
                        value={labNameFilter === 'all' ? 'All Laboratories' : labNameFilter}
                        onChange={(event, newValue) => {
                            setLabNameFilter(newValue === 'All Laboratories' || newValue === null ? 'all' : newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Laboratory Name"
                                placeholder="Type or select lab name"
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option === value}
                        clearOnEscape
                        disableClearable={false}
                    />

                    <Autocomplete
                        sx={{ minWidth: 200 }}
                        size="small"
                        options={['All States', ...getUniqueStates()]}
                        value={stateFilter === 'all' ? 'All States' : stateFilter}
                        onChange={(event, newValue) => {
                            setStateFilter(newValue === 'All States' || newValue === null ? 'all' : newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by State"
                                placeholder="Type or select state"
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option === value}
                        clearOnEscape
                        disableClearable={false}
                    />

                    <Autocomplete
                        sx={{ minWidth: 200 }}
                        size="small"
                        options={['All Days', ...getDaysPassedOptions()]}
                        value={daysPassedFilter === 'all' ? 'All Days' : daysPassedFilter}
                        onChange={(event, newValue) => {
                            setDaysPassedFilter(newValue === 'All Days' || newValue === null ? 'all' : newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter by Days Passed"
                                placeholder="Select days range"
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option === value}
                        clearOnEscape
                        disableClearable={false}
                    />

                    {(workableFilesFilter || statusFilter !== 'all' || labIdFilter !== 'all' || labNameFilter !== 'all' || stateFilter !== 'all' || daysPassedFilter !== 'all') && (
                        <Chip
                            label={`Filtered: ${filteredApplications.length} of ${applications.length}${workableFilesFilter ? ' (Workable Files)' : ''}`}
                            color="primary"
                            variant="outlined"
                            onDelete={() => {
                                setWorkableFilesFilter(false);
                                setStatusFilter('all');
                                setLabIdFilter('all');
                                setLabNameFilter('all');
                                setStateFilter('all');
                                setDaysPassedFilter('all');
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Applications Table */}
            {!loading && applications.length > 0 && (
                <>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                            {DoApiService.getButtonTypeDisplayName(selectedButtonType)}
                            <Chip
                                label={statusFilter === 'all' ? `${totalCount} records` : `${filteredApplications.length} of ${totalCount} records`}
                                size="small"
                                sx={{ ml: 1 }}
                                color="primary"
                                variant="outlined"
                            />
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                Page {page} of {totalPages}
                            </Typography>
                        </Box>
                    </Box>

                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: 3,
                            borderRadius: 2,
                            overflow: 'auto'
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            width: 80,
                                            textAlign: 'center'
                                        }}
                                    >
                                        Sr. No. / Actions
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 200
                                        }}
                                    >
                                        Lab Details
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 200
                                        }}
                                    >
                                        Laboratory Name
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 250
                                        }}
                                    >
                                        Location Details
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: 'bisque',
                                            minWidth: 120
                                        }}
                                    >
                                        Status
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 120
                                        }}
                                    >
                                        Status Date
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 100
                                        }}
                                    >
                                        Field
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 150
                                        }}
                                    >
                                        Application Submitted on
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 150
                                        }}
                                    >
                                        Assessment Start Date
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#f5f5f5',
                                            minWidth: 150
                                        }}
                                    >
                                        Assessment End Date
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredApplications.map((app, index) => (
                                    <TableRow
                                        key={app.appId}
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#fafafa'
                                            },
                                            '&:hover': {
                                                backgroundColor: '#f0f8ff',
                                                cursor: 'pointer'
                                            }
                                        }}
                                    >
                                        <TableCell sx={{ textAlign: 'center', width: 80 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.75rem' }}>
                                                    {(page - 1) * pageSize + index + 1}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column' }}>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => viewApplication(app)}
                                                    >
                                                        View
                                                    </Button>
                                                    {app.btnScf === "true" && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            onClick={() => viewSCFDashboard(app)}
                                                        >
                                                            SCF Dashboard
                                                        </Button>

                                                    )}

                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                    Lab ID: {app.applicationNo}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                    UserName: {app.loginName || app.contactPerson || 'N/A'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                        Standard:
                                                    </Typography>
                                                    <Chip
                                                        label={app.standard || ''}
                                                        size="small"
                                                        variant="outlined"
                                                        color="info"
                                                        sx={{ height: '20px', fontSize: '0.75rem' }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={app.assessmentType || getApplicationTypeName(app.assessmentTypeId)}
                                                        size="small"
                                                        color={app.assessmentTypeId === 1 ? 'primary' : 'success'}
                                                        variant="filled"
                                                        sx={{ height: '20px', fontSize: '0.75rem' }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    {(() => {
                                                        // Check if stage is 18, 18b, or 18c
                                                        const stage = app.stage || app.status || '';
                                                        const shouldShowDays = stage === '18' || stage === '18b' || stage === '18c' ||
                                                            stage === 18 || stage.toString().toLowerCase() === '18b' ||
                                                            stage.toString().toLowerCase() === '18c';

                                                        // if (!shouldShowDays) {
                                                        //     return null;
                                                        // }

                                                        const days = calculateDaysPassed(app.lastUpdated[1] || app.applicationDate);

                                                        const isNumeric = typeof days === 'number';
                                                        const bgColor = isNumeric
                                                            ? (days > 2 ? '#ffebee' : days > 1 ? '#fff3e0' : '#e8f5e9')
                                                            : '#f5f5f5';
                                                        const textColor = isNumeric
                                                            ? (days > 2 ? '#c62828' : days > 1 ? '#ef6c00' : '#2e7d32')
                                                            : '#757575';

                                                        return (
                                                            <Box
                                                                sx={{
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    backgroundColor: bgColor,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                    minWidth: '80px'
                                                                }}
                                                            >
                                                                <AccessTimeIcon
                                                                    sx={{
                                                                        fontSize: '1rem',
                                                                        color: textColor
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 'bold',
                                                                        fontSize: '0.875rem',
                                                                        color: textColor
                                                                    }}
                                                                >
                                                                    {isNumeric ? `${days} days Passed` : days}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })()}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {app.laboratoryName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'medium' }}>
                                                    {app.address || app.addressLine1 || ''}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={app.city || 'N/A'}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        sx={{
                                                            height: '20px',
                                                            fontSize: '0.7rem',
                                                            '& .MuiChip-label': { px: 1 }
                                                        }}
                                                    />
                                                    <Chip
                                                        label={app.state || 'N/A'}
                                                        size="small"
                                                        variant="filled"
                                                        color="secondary"
                                                        sx={{
                                                            height: '20px',
                                                            fontSize: '0.7rem',
                                                            '& .MuiChip-label': { px: 1 }
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={app.status || app.stage || 'Pending'}
                                                color='warning'
                                                size="small"
                                                variant="filled"
                                                sx={{
                                                    height: 'auto',
                                                    '& .MuiChip-label': {
                                                        whiteSpace: 'normal',
                                                        wordWrap: 'break-word'
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {app.lastUpdated[0]|| formatDate(app.applicationDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {app.labType || 'N/A'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(app.submitDate)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatAssessmentDate(app.assessmentStart)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatAssessmentDate(app.assessment)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>

                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
                        Showing {filteredApplications.length} of {totalCount} applications
                        {(workableFilesFilter || statusFilter !== 'all' || labIdFilter !== 'all' || labNameFilter !== 'all' || stateFilter !== 'all' || daysPassedFilter !== 'all') && (
                            <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
                                {' '}(Filtered by:
                                {workableFilesFilter && ' Workable Files'}
                                {workableFilesFilter && (statusFilter !== 'all' || labIdFilter !== 'all' || labNameFilter !== 'all' || stateFilter !== 'all' || daysPassedFilter !== 'all') && ','}
                                {statusFilter !== 'all' && ` Status: ${statusFilter}`}
                                {statusFilter !== 'all' && (labIdFilter !== 'all' || labNameFilter !== 'all' || stateFilter !== 'all' || daysPassedFilter !== 'all') && ','}
                                {labIdFilter !== 'all' && ` Lab ID: ${labIdFilter}`}
                                {labIdFilter !== 'all' && (labNameFilter !== 'all' || stateFilter !== 'all' || daysPassedFilter !== 'all') && ','}
                                {labNameFilter !== 'all' && ` Lab: ${labNameFilter}`}
                                {labNameFilter !== 'all' && (stateFilter !== 'all' || daysPassedFilter !== 'all') && ','}
                                {stateFilter !== 'all' && ` State: ${stateFilter}`}
                                {stateFilter !== 'all' && daysPassedFilter !== 'all' && ','}
                                {daysPassedFilter !== 'all' && ` Days: ${daysPassedFilter}`})
                            </span>
                        )}
                    </Typography>
                </>
            )}

            {/* No Data State */}
            {!loading && applications.length === 0 && !error && (
                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <AddCircleOutlineIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                        <Typography variant="h6" color="textSecondary">
                            No applications found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {selectedButtonType === 'btnApplicantLab'
                                ? 'No new applications are currently assigned to you.'
                                : 'No accredited applications are currently assigned to you.'
                            }
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={refreshData}
                            startIcon={<RefreshIcon />}
                            sx={{ mt: 2 }}
                        >
                            Refresh Data
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Application Details Dialog */}
            <Dialog
                open={detailsDialogOpen}
                onClose={() => setDetailsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Application Details</DialogTitle>
                <DialogContent>
                    {selectedApplication && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Laboratory Name:</Typography>
                                <Typography variant="body2">{selectedApplication.laboratoryName}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Application ID:</Typography>
                                <Typography variant="body2">{selectedApplication.appId}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Contact Person:</Typography>
                                <Typography variant="body2">{selectedApplication.contactPerson}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Contact Email:</Typography>
                                <Typography variant="body2">{selectedApplication.contactEmail}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Contact Mobile:</Typography>
                                <Typography variant="body2">{selectedApplication.contactMobile}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Application Date:</Typography>
                                <Typography variant="body2">{formatDate(selectedApplication.applicationDate)}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">Address:</Typography>
                                <Typography variant="body2">
                                    {selectedApplication.address?.addressLine1}
                                    {selectedApplication.address?.addressLine2 && ', ' + selectedApplication.address.addressLine2}
                                    {selectedApplication.address?.city && ', ' + selectedApplication.address.city}
                                    {selectedApplication.address?.state && ', ' + selectedApplication.address.state}
                                    {selectedApplication.address?.pincode && ' - ' + selectedApplication.address.pincode}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Application History Dialog */}
            <Dialog
                open={historyDialogOpen}
                onClose={() => setHistoryDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Application Process History</DialogTitle>
                <DialogContent>
                    {applicationHistory.length > 0 ? (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Stage</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Remarks</TableCell>
                                        <TableCell>Updated By</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {applicationHistory.map((history, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{formatDate(history.createdDate)}</TableCell>
                                            <TableCell>{history.stage}</TableCell>
                                            <TableCell>{history.status}</TableCell>
                                            <TableCell>{history.remarks || 'N/A'}</TableCell>
                                            <TableCell>{history.createdByUserName || history.createdBy}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography>No history available for this application.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
export default SubmittedApplicationSection;
